import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getDeviceApplications } from '../../api/internal/usersInternalApi';
import {
    Card, CardTitle, Button, IconButton, Pill, PillDot, Input, FieldLabel,
    Notice, EmptyState, Spinner, SpinnerContainer, DataTable, TableScroll,
} from '../ui';
import { FilterBar, Muted, Pager, PathToggle } from './security.styled';
import { formatDate } from './severity';
import { downloadCsv } from './csvDownload';
import { versionEstInferieure } from './versionCompare';

const PAGE_SIZE = 100;

// Ton du badge d'état de déploiement. Les libellés viennent d'Intune tels
// quels — espaces et casse compris, « Not installed » et non « notInstalled ».
const ETAT_TONE = {
    Installed: 'ok',
    Failed: 'danger',
    'Not installed': 'warn',
    'Install Pending': 'accent',
    'Not applicable': 'neutral',
};

// Deux inventaires, pas deux vues du même : l'onglet décide lequel on regarde.
const ONGLETS = [
    { cle: 'deploiement', libelle: 'Déployées par Intune' },
    { cle: 'constatees', libelle: 'Découvertes sur le poste' },
];

// L'intention change complètement la lecture d'un état.
//
// « Non installée » sur une application REQUISE est un manquement ; sur une
// application DISPONIBLE, c'est le fonctionnement normal du portail
// d'entreprise. Mesuré le 2026-09-03 : des 6 209 lignes « Not installed » du
// parc, 147 seulement portent sur du requis. Sans cette colonne, la carte
// annonçait « 17 non installées » sur un poste sans dire que 14 d'entre elles
// n'avaient jamais eu à l'être.
const INTENT_LABEL = {
    required: 'Requise',
    available: 'Disponible',
    uninstall: 'Désinstallation',
};

function IntentPill({ intent }) {
    if (!intent) return <Muted>Sans affectation</Muted>;
    return (
        <Pill $tone={intent === 'required' ? 'accent' : 'neutral'}>
            {INTENT_LABEL[intent] || intent}
        </Pill>
    );
}

// Le paramétrage d'affectation, DÉPLIÉ dans le tableau et non caché au survol.
//
// Première version : une infobulle sur le badge d'intention. Autant dire que
// l'information n'était pas affichée — invisible sans savoir qu'il faut
// survoler, et illisible sur un écran tactile. Une application peut viser
// quinze groupes, d'où le repli sous un compteur plutôt qu'une colonne.
function ParametrageAffectation({ assignments }) {
    if (!assignments?.length) {
        return (
            <Muted>
                Aucune affectation : l'application est au catalogue mais n'est plus poussée.
                Les lignes de déploiement ci-contre sont un reste de la dernière affectation.
            </Muted>
        );
    }

    // Regroupé par libellé d'intention : « Requise » d'un côté, « Requise sauf »
    // de l'autre, « Disponible » à part. Une application visant douze étoiles
    // produit douze lignes identiques dans l'intention — c'est la liste des
    // cibles qui porte l'information.
    const parIntention = new Map();
    assignments.forEach((a) => {
        const cle = a.intentLabel || a.intent || 'Intention inconnue';
        if (!parIntention.has(cle)) parIntention.set(cle, { isExclusion: a.isExclusion, cibles: [] });
        parIntention.get(cle).cibles.push(a.targetLabel);
    });

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {[...parIntention.entries()].map(([intention, { isExclusion, cibles }]) => (
                <div key={intention} style={{ display: 'flex', gap: '8px', alignItems: 'baseline', flexWrap: 'wrap' }}>
                    {/* L'exclusion en avertissement : c'est une exception au
                        déploiement, pas un ciblage de plus. */}
                    <Pill $tone={isExclusion ? 'warn' : intention.startsWith('Requise') ? 'accent' : 'neutral'}>
                        {isExclusion && <i className="bx bx-block" style={{ fontSize: '0.9rem' }} />}
                        {intention}
                    </Pill>
                    <Muted style={{ fontSize: '0.78rem' }}>{cibles.join(', ')}</Muted>
                </div>
            ))}
        </div>
    );
}

/**
 * Section « Applications » de la fiche appareil.
 *
 * Les deux listes sont volontairement SÉPARÉES et non rapprochées : les deux
 * inventaires n'ont aucun identifiant commun, et un rapprochement par nom ne
 * retrouvait que 5 des 45 applications déployées sur le poste témoin. Afficher
 * un croisement faux serait pire que de laisser l'utilisateur lire les deux.
 */
export default function DeviceApplications({
    applications, deviceIdIntune, deviceName, defaultExpanded = false,
}) {
    const [donnees, setDonnees] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    // Déplié d'office quand la carte occupe sa propre section : le clic sur
    // « Applications » dans la navigation EST déjà le geste de dépliage, un
    // second clic pour voir ce qu'on est venu chercher n'apporte rien. Le repli
    // reste utile quand la carte est empilée avec d'autres.
    const [expanded, setExpanded] = useState(defaultExpanded);
    const [exporting, setExporting] = useState(false);

    const [onglet, setOnglet] = useState('deploiement');
    const [search, setSearch] = useState('');
    const [echecsSeuls, setEchecsSeuls] = useState(false);
    const [requisesSeules, setRequisesSeules] = useState(false);
    const [versionBelow, setVersionBelow] = useState('');
    const [page, setPage] = useState(1);
    // Repli par application : le paramétrage d'affectation est verbeux (jusqu'à
    // quinze cibles) et on ne consulte celui que d'une application à la fois.
    const [affectationsOuvertes, setAffectationsOuvertes] = useState({});

    const chargement = useRef(null);

    const charger = () => {
        if (donnees) return Promise.resolve(donnees);
        if (chargement.current) return chargement.current;

        setLoading(true);
        setError(null);
        chargement.current = getDeviceApplications(deviceIdIntune)
            .then((res) => { setDonnees(res); return res; })
            .catch(() => { setError('Chargement des applications impossible.'); return null; })
            .finally(() => { setLoading(false); chargement.current = null; });
        return chargement.current;
    };

    // Le chargement suit l'ouverture : déplié d'office, il part au montage.
    // Gardé par une ref plutôt que par les dépendances de l'effet — `charger`
    // est recréée à chaque rendu, et la stabiliser en useCallback figerait
    // `donnees` dans sa clôture, ce qui referait l'appel à chaque export.
    const chargementInitial = useRef(false);
    useEffect(() => {
        if (!defaultExpanded || chargementInitial.current) return;
        chargementInitial.current = true;
        charger();
    }, [defaultExpanded, deviceIdIntune]);

    const basculer = () => {
        if (!expanded) {
            charger();
        } else {
            setSearch('');
            setEchecsSeuls(false);
            setRequisesSeules(false);
            setVersionBelow('');
            setAffectationsOuvertes({});
            setPage(1);
        }
        setExpanded((o) => !o);
    };

    const lignes = useMemo(() => {
        if (!donnees) return [];
        const terme = search.trim().toLowerCase();
        const cible = versionBelow.trim();
        const source = onglet === 'deploiement' ? donnees.deploiement : donnees.detectees;

        return source.filter((ligne) => {
            if (echecsSeuls && ligne.installState !== 'Failed') return false;
            if (requisesSeules && ligne.intent !== 'required') return false;
            // Les deux onglets ne portent pas la version dans le même champ :
            // celle qu'Intune a déployée d'un côté, celle constatée de l'autre.
            if (cible) {
                const version = onglet === 'deploiement' ? ligne.appVersion : ligne.version;
                if (!versionEstInferieure(version, cible)) return false;
            }
            if (!terme) return true;
            const nom = (onglet === 'deploiement' ? ligne.appName : ligne.displayName) || '';
            return nom.toLowerCase().includes(terme);
        });
    }, [donnees, onglet, search, echecsSeuls, requisesSeules, versionBelow]);

    // Ce qui est à l'écran est ce qui est exporté, filtres et onglet compris —
    // même règle que l'export des vulnérabilités. Le bouton n'apparaît que
    // déplié, donc les lignes sont déjà chargées et filtrées.
    const exporter = () => {
        if (lignes.length === 0) return;
        setExporting(true);
        try {
            if (onglet === 'deploiement') {
                downloadCsv(
                    `${deviceName || 'appareil'}_deploiement.csv`,
                    [
                        'Appareil', 'Application déployée', 'Intention', 'Affectations',
                        "État de l'installation", "Détail de l'état", 'Version installée',
                        "Code d'erreur hexadécimal", 'Utilisateur', 'Plateforme et version',
                        'Relevé le',
                    ],
                    lignes.map((l) => [
                        deviceName || '',
                        l.appName || '',
                        INTENT_LABEL[l.intent] || l.intent || '',
                        // Le paramétrage complet, exclusions comprises : c'est
                        // la colonne qui explique pourquoi l'application est
                        // attendue — ou pas — sur ce poste.
                        (l.assignments || []).map((a) => `${a.intentLabel} : ${a.targetLabel}`).join(' | '),
                        l.installState || '',
                        l.installStateDetail || '',
                        l.appVersion || '',
                        l.hexErrorCode || '',
                        l.userName || l.userPrincipalName || '',
                        l.platform || '',
                        formatDate(l.lastModifiedDateTime),
                    ])
                );
            } else {
                downloadCsv(
                    `${deviceName || 'appareil'}_applications_constatees.csv`,
                    ['Appareil', 'Application constatée', 'Version', 'Relevé le'],
                    lignes.map((l) => [
                        deviceName || '',
                        l.displayName || '',
                        l.version || '',
                        formatDate(l.lastSync),
                    ])
                );
            }
        } finally {
            setExporting(false);
        }
    };

    if (!applications?.managedByIntune) return null;

    const totalPages = Math.max(Math.ceil(lignes.length / PAGE_SIZE), 1);
    const pageCourante = Math.min(page, totalPages);
    const visibles = lignes.slice((pageCourante - 1) * PAGE_SIZE, pageCourante * PAGE_SIZE);
    const majFiltre = (setter) => (valeur) => { setter(valeur); setPage(1); };

    return (
        <Card $pad="16px">
            <CardTitle style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                Applications
                {/* Le ton suit les applications REQUISES en échec, pas le total :
                    un échec sur une application simplement mise à disposition ne
                    justifie pas d'alerter. */}
                <Pill $tone={applications.requisesEnEchec > 0 ? 'danger' : 'ok'}>
                    <PillDot />
                    {applications.installees} installée{applications.installees > 1 ? 's' : ''} sur {applications.deployees} déployée{applications.deployees > 1 ? 's' : ''}
                </Pill>
                {applications.requisesEnEchec > 0 && (
                    <Pill $tone="danger">
                        <PillDot />
                        {applications.requisesEnEchec} requise{applications.requisesEnEchec > 1 ? 's' : ''} en échec
                    </Pill>
                )}
                {applications.requisesNonInstallees > 0 && (
                    <Pill $tone="warn">
                        {applications.requisesNonInstallees} requise{applications.requisesNonInstallees > 1 ? 's' : ''} non installée{applications.requisesNonInstallees > 1 ? 's' : ''}
                    </Pill>
                )}
                {/* Les échecs sur du disponible existent aussi, mais en second
                    rang : signalés sans être alarmants. */}
                {applications.enEchec > applications.requisesEnEchec && (
                    <Pill $tone="neutral">
                        {applications.enEchec - applications.requisesEnEchec} en échec sur du disponible
                    </Pill>
                )}
                <Pill $tone="neutral">{applications.detectees.toLocaleString('fr-FR')} découvertes</Pill>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
                    <Muted style={{ fontWeight: 400, fontSize: '0.72rem' }}>
                        {applications.lastSync
                            ? `Relevé le ${formatDate(applications.lastSync)}`
                            : 'Jamais relevé'}
                    </Muted>
                    <Button $variant="secondary" onClick={basculer} disabled={loading}>
                        {loading && !expanded
                            ? <Spinner $size="14px" />
                            : <i className={expanded ? 'bx bx-chevron-up' : 'bx bx-chevron-down'} />}
                        {expanded ? 'Réduire' : 'Afficher le détail'}
                    </Button>
                    {expanded && (
                        <Button $variant="secondary" onClick={exporter} disabled={exporting}>
                            {exporting ? <Spinner $size="14px" /> : <i className="bx bxs-download" />}
                            Exporter en CSV
                        </Button>
                    )}
                </div>
            </CardTitle>

            {/* L'inventaire constaté ne tourne qu'une fois par nuit : un poste
                enrôlé dans la journée n'y figure pas encore, et le dire évite de
                faire chercher une panne là où il n'y a qu'un décalage. */}
            {applications.detectees === 0 && applications.deployees > 0 && (
                <Notice $tone="accent" style={{ marginBottom: '12px' }}>
                    <i className="bx bx-info-circle" />
                    <span>
                        Aucune application constatée sur ce poste. L'inventaire logiciel est
                        collecté une fois par nuit : un appareil enrôlé récemment n'y figure
                        pas encore. Le déploiement Intune, lui, est rafraîchi chaque heure.
                    </span>
                </Notice>
            )}

            {error && (
                <Notice $tone="danger" style={{ marginBottom: '12px' }}>
                    <i className="bx bx-error-circle" />{error}
                </Notice>
            )}

            {!expanded ? (
                <Muted style={{ fontSize: '0.78rem' }}>
                    {/* Les non installées sont ventilées : sans ça, le chiffre brut
                        fait passer pour un problème une mise à disposition que
                        personne n'a eu à installer. */}
                    {applications.nonInstallees > applications.requisesNonInstallees
                        && `${applications.nonInstallees - applications.requisesNonInstallees} application${applications.nonInstallees - applications.requisesNonInstallees > 1 ? 's' : ''} proposée${applications.nonInstallees - applications.requisesNonInstallees > 1 ? 's' : ''} et non installée${applications.nonInstallees - applications.requisesNonInstallees > 1 ? 's' : ''} — c'est normal pour du disponible. `}
                    {applications.nonApplicables > 0 && `${applications.nonApplicables} non applicable${applications.nonApplicables > 1 ? 's' : ''}. `}
                    {applications.enAttente > 0 && `${applications.enAttente} en attente. `}
                    Deux inventaires distincts : ce qu'Intune déploie et ce qui est réellement
                    présent sur le poste.
                </Muted>
            ) : loading ? (
                <SpinnerContainer><Spinner /></SpinnerContainer>
            ) : donnees ? (
                <>
                    <FilterBar style={{ marginBottom: '14px' }}>
                        <div style={{ display: 'flex', gap: '6px' }}>
                            {ONGLETS.map(({ cle, libelle }) => (
                                <Button
                                    key={cle}
                                    $variant={onglet === cle ? 'primary' : 'secondary'}
                                    onClick={() => { setOnglet(cle); setPage(1); setEchecsSeuls(false); setRequisesSeules(false); }}
                                >
                                    {libelle}
                                    {' '}
                                    ({(cle === 'deploiement' ? donnees.deploiement.length : donnees.detectees.length).toLocaleString('fr-FR')})
                                </Button>
                            ))}
                        </div>
                        <div style={{ flex: 1, minWidth: '200px' }}>
                            <FieldLabel htmlFor="app-search">Recherche</FieldLabel>
                            <Input
                                id="app-search"
                                style={{ width: '100%', marginTop: '6px' }}
                                placeholder="Nom d'application..."
                                value={search}
                                onChange={(e) => majFiltre(setSearch)(e.target.value)}
                            />
                        </div>
                        {/* « Quelle application est en retard de version sur ce
                            poste » : une comparaison de chaînes répondrait faux,
                            7.10 étant postérieure à 7.4 tout en étant plus
                            petite lexicographiquement. Une version illisible ou
                            absente est retenue — c'est un cas à examiner, pas à
                            écarter. */}
                        <div style={{ width: '150px' }}>
                            <FieldLabel htmlFor="app-version">Version inférieure à</FieldLabel>
                            <Input
                                id="app-version"
                                style={{ width: '100%', marginTop: '6px' }}
                                placeholder="ex. 7.4.3"
                                value={versionBelow}
                                onChange={(e) => majFiltre(setVersionBelow)(e.target.value)}
                            />
                        </div>
                        {onglet === 'deploiement' && (
                            <>
                                <label htmlFor="app-requises">
                                    <input
                                        id="app-requises"
                                        type="checkbox"
                                        checked={requisesSeules}
                                        onChange={(e) => majFiltre(setRequisesSeules)(e.target.checked)}
                                    />
                                    Requises uniquement
                                </label>
                                <label htmlFor="app-echecs">
                                    <input
                                        id="app-echecs"
                                        type="checkbox"
                                        checked={echecsSeuls}
                                        onChange={(e) => majFiltre(setEchecsSeuls)(e.target.checked)}
                                    />
                                    Échecs uniquement
                                </label>
                            </>
                        )}
                    </FilterBar>

                    {visibles.length === 0 ? (
                        <EmptyState>
                            <i className="bx bx-package" />
                            <h3>Aucune application</h3>
                            <p>Aucune application ne correspond à ces critères.</p>
                        </EmptyState>
                    ) : (
                        <>
                            <TableScroll>
                                <DataTable>
                                    <thead>
                                        {onglet === 'deploiement' ? (
                                            <tr>
                                                <th>Application</th>
                                                <th>Intention</th>
                                                <th>État</th>
                                                <th>Détail</th>
                                                <th>Version</th>
                                                <th>Utilisateur</th>
                                                <th>Relevé le</th>
                                            </tr>
                                        ) : (
                                            <tr>
                                                <th>Application</th>
                                                <th>Version</th>
                                            </tr>
                                        )}
                                    </thead>
                                    <tbody>
                                        {onglet === 'deploiement'
                                            ? visibles.flatMap((l) => {
                                                const ouvert = Boolean(affectationsOuvertes[l.appId]);
                                                const nbCibles = (l.assignments || []).length;
                                                return [
                                                    <tr key={l.appId}>
                                                        <td>{l.appName || <Muted>Sans nom</Muted>}</td>
                                                        <td>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                                                <IntentPill intent={l.intent} />
                                                                {/* Une exclusion ne modifie pas l'intention
                                                                    retenue, mais la taire ferait chercher
                                                                    pourquoi l'application n'arrive pas. */}
                                                                {l.aDesExclusions && (
                                                                    <Pill $tone="warn" title="Une exception exclut au moins un groupe de ce déploiement">
                                                                        <i className="bx bx-block" style={{ fontSize: '0.9rem' }} />
                                                                        exception
                                                                    </Pill>
                                                                )}
                                                                {nbCibles > 0 && (
                                                                    <PathToggle
                                                                        type="button"
                                                                        onClick={() => setAffectationsOuvertes((p) => ({ ...p, [l.appId]: !ouvert }))}
                                                                        title={ouvert ? 'Masquer le paramétrage' : 'Afficher le paramétrage de cette affectation'}
                                                                    >
                                                                        <i className={ouvert ? 'bx bx-chevron-down' : 'bx bx-chevron-right'} />
                                                                        {nbCibles} cible{nbCibles > 1 ? 's' : ''}
                                                                    </PathToggle>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <Pill $tone={ETAT_TONE[l.installState] || 'neutral'}>
                                                                <PillDot />{l.installState || 'Inconnu'}
                                                            </Pill>
                                                        </td>
                                                        <td>
                                                            {l.installStateDetail
                                                                ? <Muted>{l.installStateDetail}</Muted>
                                                                : l.hexErrorCode
                                                                    ? <Muted>{l.hexErrorCode}</Muted>
                                                                    : <Muted>—</Muted>}
                                                        </td>
                                                        <td><Muted>{l.appVersion || '—'}</Muted></td>
                                                        <td><Muted>{l.userName || l.userPrincipalName || '—'}</Muted></td>
                                                        <td><Muted>{formatDate(l.lastModifiedDateTime)}</Muted></td>
                                                    </tr>,
                                                    ouvert && (
                                                        <tr key={`${l.appId}-affectations`}>
                                                            <td />
                                                            {/* Pas de paddingTop à zéro ici : le badge
                                                                d'intention venait alors coller la bordure
                                                                de la ligne du dessus. Le rythme vertical
                                                                du tableau (9 px) suffit à rattacher le
                                                                détail à sa ligne sans le souder. */}
                                                            <td colSpan={6} style={{ paddingBottom: '12px' }}>
                                                                <ParametrageAffectation assignments={l.assignments} />
                                                            </td>
                                                        </tr>
                                                    ),
                                                ].filter(Boolean);
                                            })
                                            : visibles.map((l) => (
                                                <tr key={l.detectedAppId}>
                                                    <td>{l.displayName || <Muted>Sans nom</Muted>}</td>
                                                    <td><Muted>{l.version || '—'}</Muted></td>
                                                </tr>
                                            ))}
                                    </tbody>
                                </DataTable>
                            </TableScroll>

                            <Pager>
                                <span><strong>{lignes.length.toLocaleString('fr-FR')}</strong> applications</span>
                                <div className="spacer" />
                                <span>Page <strong>{pageCourante}</strong> / {totalPages}</span>
                                <IconButton
                                    $bordered
                                    disabled={pageCourante <= 1}
                                    onClick={() => setPage(pageCourante - 1)}
                                    title="Page précédente"
                                >
                                    <i className="bx bx-chevron-left" />
                                </IconButton>
                                <IconButton
                                    $bordered
                                    disabled={pageCourante >= totalPages}
                                    onClick={() => setPage(pageCourante + 1)}
                                    title="Page suivante"
                                >
                                    <i className="bx bx-chevron-right" />
                                </IconButton>
                            </Pager>
                        </>
                    )}
                </>
            ) : null}
        </Card>
    );
}
